/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Clock3 } from 'lucide-react';
import { PageRoute } from '../types';

interface DomilyProps {
  setRoute: (route: PageRoute) => void;
}

export default function Domily({ setRoute }: DomilyProps) {
  const openDomi = () => {
    setRoute('domi');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div id="domily-page" className="bg-[#111111] text-white font-sans antialiased">
      {/* Hero */}
      <section className="relative min-h-[460px] md:min-h-[560px] flex items-center overflow-hidden -mt-[72px] pt-[72px]">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/domily/domily-hero.webp')" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, rgba(10,10,10,0.72) 0%, rgba(10,10,10,0.56) 38%, rgba(10,10,10,0.30) 68%, rgba(10,10,10,0.18) 100%), rgba(10,10,10,0.10)',
          }}
        />
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-[720px] text-left"
          >
            <span className="inline-block text-xs tracking-[0.28em] font-bold text-[#00F5D4] mb-4">
              Koemoriの仲間たち
            </span>
            <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mb-5">
              <span className="bg-gradient-to-br from-[#00F5D4] to-[#00BFA0] bg-clip-text text-transparent">
              声でつながって、
              </span>
              <br />
              いっしょに歌おう！
            </h1>
            <p className="text-[0.9375rem] md:text-[1.0625rem] text-white/80 leading-[1.85] max-w-[600px]">
              アプリでDomilyのみんなと一緒に、歌声を重ねてみませんか？
            </p>
          </motion.div>
        </div>
      </section>

      {/* IP メンバー — 单列上下堆叠的全宽横卡 */}
      <section className="bg-[#0A0A0A] py-16 md:py-24">
        <div className="w-full max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-bold leading-[1.4] mb-4">
              Domily
              <span className="bg-gradient-to-br from-[#00F5D4] to-[#00BFA0] bg-clip-text text-transparent">
                メンバー
              </span>
            </h2>
            <p className="text-base text-white/75 max-w-[640px] mx-auto leading-[1.7]">
              ドミと愉快な仲間たち、それが「Domily」！
            </p>
          </div>

          <div className="flex flex-col gap-8 max-w-[1120px] mx-auto">
            {/* Domi */}
            <motion.button
              type="button"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45 }}
              onClick={openDomi}
              className="group flex w-full flex-col md:flex-row text-left overflow-hidden rounded-3xl border border-white/[0.06] bg-[#1A1A1A] min-h-0 md:min-h-[320px] hover:-translate-y-1 hover:border-[#00F5D4]/20 hover:shadow-[0_12px_40px_rgba(0,245,212,0.12)] transition-all cursor-pointer"
            >
              <div className="relative w-full aspect-square md:aspect-auto md:w-[320px] md:min-w-[320px] md:min-h-[320px] shrink-0 overflow-hidden flex items-center justify-center bg-transparent">
                <img
                  src="/domily/domily-domi-card.webp"
                  alt="Domi"
                  className="w-full h-full object-contain object-center"
                />
              </div>
              <div className="flex-1 flex flex-col justify-center px-6 py-6 md:px-10 md:py-9">
                <span className="text-xs font-semibold tracking-[0.08em] text-[#00F5D4] mb-2">
                  a fairy born from singing
                </span>
                <h3 className="text-[1.25rem] md:text-2xl font-bold leading-[1.4] mb-4 text-white">
                  ドミー｜うまい・下手より、楽しさがいちばん！
                </h3>
                <p className="text-[0.9375rem] text-white/75 leading-[1.75] mb-6">
                Domilyの頼れるリーダー的存在。歌うことが大好きで、みんなが笑顔になれる時間を大切にする心優しい妖精。みんなと歌声を重ねるとき、いちばんキラキラ輝くのはドミーだよ。
                </p>
                <span className="inline-flex items-center gap-2 text-[0.9375rem] font-semibold text-[#00F5D4] group-hover:gap-3 transition-all">
                  もっと知りたい
                  <ArrowRight className="w-[18px] h-[18px]" />
                </span>
              </div>
            </motion.button>

            {/* Coming soon */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: 0.06 }}
              className="flex w-full flex-col md:flex-row overflow-hidden rounded-3xl border border-dashed border-white/[0.08] bg-[#1A1A1A] min-h-0 md:min-h-[320px]"
            >
              <div className="relative w-full aspect-square md:aspect-auto md:w-[320px] md:min-w-[320px] md:min-h-[320px] shrink-0 overflow-hidden flex items-center justify-center bg-transparent">
                <img
                  src="/domily/domily-coming-soon.webp"
                  alt="COMING SOON"
                  className="w-full h-full object-contain object-center"
                />
              </div>
              <div className="flex-1 flex flex-col justify-center px-6 py-6 md:px-10 md:py-9">
                <span className="text-xs font-semibold tracking-[0.08em] text-white/45 mb-2">
                  COMING SOON
                </span>
                <h3 className="text-[1.25rem] md:text-2xl font-bold leading-[1.4] text-white mb-4">
                  新しい仲間がもうすぐ登場
                </h3>
                <p className="text-[0.9375rem] text-white/75 leading-[1.75] mb-4">
                個性豊かな仲間たちが、まもなくやってきます。どうぞお楽しみに！
                </p>
                <span className="inline-flex items-center gap-1.5 self-start mt-2 px-3.5 py-1 rounded-full text-[0.8125rem] font-semibold text-white/45 bg-white/[0.04]">
                  <Clock3 className="w-3.5 h-3.5" />
                  近日公開
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
